// 📁 EmployesPage.js
import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "../context/AxiosInstance";
import { Table, Button, Form, Spinner, Image } from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import Sidebar from "../components/Sidebar";
import EmployeFormModal from "../components/employe/EmployeFormModal";
import ConfirmDeleteModal from "../components/employe/ConfirmDeleteModal";
import RhKpiCard from "../components/common/RhKpiCard";
import RhTablePagination from "../components/common/RhTablePagination";
import { FaUsers, FaSearch, FaUserPlus, FaPen, FaTrash } from "react-icons/fa";
import "../styles/rh-modern-ui.css";
import "./EmployesPage.css";

const isEmployeActif = (emp) => {
  const s = String(emp?.date_sortie || "").trim();
  return !s || s === "0000-00-00" || s.startsWith("0000-");
};

const getInitials = (nom, prenom) =>
  `${(prenom || "").charAt(0)}${(nom || "").charAt(0)}`.toUpperCase() || "?";

const EmployesPage = () => {
  const { token, role } = useContext(AuthContext);

  const [employes, setEmployes] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEmploye, setSelectedEmploye] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchEmployes = async () => {
    setLoading(true);
    if (role !== "RH" && role !== "Assistant" && role !== "ASSISTANT") {
      setUnauthorized(true);
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get("/employes/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployes(res.data.data);
    } catch (err) {
      console.error("Erreur de chargement :", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && role) fetchEmployes();
  }, [token, role]);

  const openForm = (emp = null) => {
    setSelectedEmploye(emp);
    setIsEditMode(!!emp);
    setShowFormModal(true);
  };

  const openDelete = (emp) => {
    setSelectedEmploye(emp);
    setShowDeleteModal(true);
  };

  const filtered = employes.filter((emp) =>
    `${emp.nom} ${emp.prenom} ${emp.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const kpis = useMemo(() => {
    const list = employes || [];
    const actifs = list.filter(isEmployeActif).length;
    const masculin = list.filter((e) => (e.genre || "").toLowerCase().startsWith("m")).length;
    const feminin = list.filter((e) => (e.genre || "").toLowerCase().startsWith("f")).length;
    const fonctionnaires = list.filter((e) => e.type_cadre === "Fonctionnaire").length;
    const contractuels = list.filter((e) => e.type_cadre === "Contractuel").length;
    const avecPhoto = list.filter((e) => e.photo_employe).length;
    return { total: list.length, actifs, masculin, feminin, fonctionnaires, contractuels, avecPhoto };
  }, [employes]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const currentData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const canEdit = role === "RH" || role === "Assistant" || role === "ASSISTANT";

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Navbar />
        <div className="container-fluid p-4 p-lg-5 rh-page-content">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Employés" }]} />

          <header className="rh-page-header">
            <h4>
              <span className="rh-page-icon"><FaUsers /></span>
              Gestion des employés
            </h4>
            <p>Vue d&apos;ensemble de l&apos;effectif, recherche rapide et gestion des fiches employés.</p>
          </header>

          {loading ? (
            <div className="rh-loading-block">
              <Spinner animation="border" />
              <p className="mt-3 mb-0">Chargement des employés…</p>
            </div>
          ) : unauthorized ? (
            <div className="rh-alert-unauth">Données non permises pour cet utilisateur</div>
          ) : (
            <>
              <div className="rh-kpi-grid">
                <RhKpiCard label="Total effectif" value={kpis.total} variant="primary" sub="Tous les employés enregistrés" />
                <RhKpiCard label="En poste" value={kpis.actifs} variant="success" sub="Sans date de sortie" />
                <RhKpiCard label="Hommes" value={kpis.masculin} variant="info" />
                <RhKpiCard label="Femmes" value={kpis.feminin} variant="info" />
                <RhKpiCard label="Fonctionnaires" value={kpis.fonctionnaires} variant="neutral" />
                <RhKpiCard label="Contractuels" value={kpis.contractuels} variant="neutral" />
                <RhKpiCard
                  label="Avec photo"
                  value={kpis.avecPhoto}
                  variant="warning"
                  sub={kpis.total ? `${Math.round((kpis.avecPhoto / kpis.total) * 100)} % du total` : "—"}
                />
              </div>

              <div className="rh-toolbar">
                <div className="rh-toolbar-left">
                  <div className="rh-search-wrap">
                    <FaSearch className="rh-search-icon" />
                    <Form.Control
                      type="text"
                      placeholder="Rechercher nom, prénom ou email…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <span className="rh-result-pill">{filtered.length} résultat{filtered.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="rh-toolbar-actions">
                  <Button className="rh-btn-primary" onClick={() => openForm()}>
                    <FaUserPlus /> Ajouter un employé
                  </Button>
                </div>
              </div>

              <div className="rh-table-panel">
                <div className="table-responsive">
                  <Table hover className="rh-modern-table mb-0">
                    <thead>
                      <tr>
                        <th>Photo</th>
                        <th>Nom</th>
                        <th>Prénom</th>
                        <th>Email</th>
                        <th>Contact</th>
                        <th>Matricule</th>
                        <th>Type</th>
                        <th>Genre</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentData.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center text-muted py-4">
                            Aucun employé ne correspond à votre recherche.
                          </td>
                        </tr>
                      ) : (
                        currentData.map((emp) => (
                          <tr key={emp.id_employe}>
                            <td>
                              {emp.photo_employe ? (
                                <Image
                                  src={`/api/static/photos/${emp.photo_employe}`}
                                  className="rh-avatar"
                                  alt=""
                                />
                              ) : (
                                <span className="rh-avatar-initials">{getInitials(emp.nom, emp.prenom)}</span>
                              )}
                            </td>
                            <td><strong>{emp.nom}</strong></td>
                            <td>{emp.prenom}</td>
                            <td>{emp.email}</td>
                            <td>{emp.contact}</td>
                            <td><span className="rh-badge rh-badge-slate">{emp.matricule_employe || "—"}</span></td>
                            <td>
                              {emp.type_cadre ? (
                                <span className={`rh-badge ${emp.type_cadre === "Fonctionnaire" ? "rh-badge-indigo" : "rh-badge-green"}`}>
                                  {emp.type_cadre}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>{emp.genre || "—"}</td>
                            <td>
                              <div className="rh-row-actions">
                                {canEdit ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline-success"
                                      className="rh-action-btn"
                                      onClick={() => openForm(emp)}
                                    >
                                      <FaPen /> Modifier
                                    </Button>
                                    {role === "RH" && (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        className="rh-action-btn"
                                        onClick={() => openDelete(emp)}
                                      >
                                        <FaTrash /> Supprimer
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted small">Aucune action</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                <RhTablePagination
                  currentPage={currentPage}
                  totalItems={filtered.length}
                  perPage={perPage}
                  onPageChange={setCurrentPage}
                  onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  itemLabel="employés"
                />
              </div>
            </>
          )}
        </div>

        <EmployeFormModal
          show={showFormModal}
          handleClose={() => setShowFormModal(false)}
          isEditMode={isEditMode}
          selectedEmploye={selectedEmploye}
          token={token}
          refreshData={fetchEmployes}
        />

        <ConfirmDeleteModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          employe={selectedEmploye}
          token={token}
          refreshData={fetchEmployes}
        />
      </div>
    </div>
  );
};

export default EmployesPage;
